import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, Plus, FileText, Upload, ShieldCheck, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CURRENCIES = ["EUR", "USD", "TRY", "GBP"];
const METHODS = [["cash", "m_cash"], ["credit_card", "m_credit_card"], ["debit_card", "m_debit_card"]];
const money = (a, c) => new Intl.NumberFormat("en-IE", { style: "currency", currency: c || "EUR" }).format(a || 0);

export default function Payments() {
  const { can } = useAuth();
  const t = useT();
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [payments, setPayments] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [passport, setPassport] = useState({ exists: false });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [form, setForm] = useState({ amount: "", currency: "EUR", method: "cash", pos_reference: "", notes: "" });
  const fileRef = useRef(null);
  const canCreate = can("financial_records", "create");
  const canClose = can("financial_records", "edit");

  useEffect(() => { api.get("/patients").then((r) => { setPatients(r.data); if (r.data.length) setPatientId(r.data[0].id); }).catch(() => {}); }, []);
  const load = useCallback(() => {
    if (!patientId) return;
    api.get(`/payments?patient_id=${patientId}`).then((r) => setPayments(r.data)).catch(() => {});
    api.get(`/payments/ledger/${patientId}`).then((r) => setLedger(r.data)).catch(() => setLedger(null));
    api.get(`/payments/passport/${patientId}`).then((r) => setPassport(r.data)).catch(() => setPassport({ exists: false }));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const closeTreatment = async () => {
    setClosing(true);
    try {
      await api.post(`/payments/close-treatment/${patientId}`);
      toast.success(t("payments.treatmentFinClosed"));
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.remaining_balance != null) {
        toast.error(`${t("payments.cannotClose")} ${money(d.remaining_balance, d.currency)} ${t("payments.stillOutstanding")}`);
      } else { toast.error(formatApiError(d)); }
    } finally { setClosing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount) return toast.error(t("payments.enterAmount"));
    setSaving(true);
    try {
      const { data } = await api.post("/payments", { patient_id: patientId, ...form, amount: Number(form.amount) });
      toast.success(`${t("payments.paymentRecorded")} (${data.reference})`);
      if (!data.passport_on_file) toast.warning(t("payments.noPassportWarn"));
      setOpen(false); setForm({ amount: "", currency: "EUR", method: "cash", pos_reference: "", notes: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const genReceipt = async (p) => {
    try { await api.post(`/payments/${p.id}/receipt`); toast.success(t("payments.receiptGenerated")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dlReceipt = async (p) => {
    try { const r = await api.get(`/payments/${p.id}/receipt/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error(t("payments.receiptNotAvailable")); }
  };
  const uploadPassport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/payments/passport/${patientId}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("payments.passportStored")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const viewPassport = async () => {
    try { const r = await api.get(`/payments/passport/${patientId}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error(t("payments.openPassportErr")); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("payments.title")} subtitle={t("payments.subtitle")}>
        <Select value={patientId} onValueChange={setPatientId}>
          <SelectTrigger className="w-64" data-testid="payment-patient-select"><SelectValue placeholder={t("payments.patient")} /></SelectTrigger>
          <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
        </Select>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-payment-button" className="bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("payments.record")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{t("payments.recordPayment")}</DialogTitle><DialogDescription>{t("payments.recordDesc")}</DialogDescription></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="payment-form">
                <div className="space-y-1.5"><Label>{t("payments.amount")}</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" /></div>
                <div className="space-y-1.5"><Label>{t("payments.currency")}</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}><SelectTrigger data-testid="payment-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>{t("payments.method")}</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}><SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{t(`payments.${l}`)}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>{t("payments.posReference")}</Label><Input value={form.pos_reference} onChange={(e) => setForm({ ...form, pos_reference: e.target.value })} data-testid="payment-pos" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("payments.notes")}</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter className="col-span-2 mt-2"><Button type="submit" disabled={saving} data-testid="payment-submit" className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("payments.recordPaymentBtn")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {ledger && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="ledger-panel">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-medium text-slate-900">{t("payments.ledger")}</h3>
            {ledger.treatment_financially_closed ? (
              <Badge className="bg-emerald-100 text-emerald-700" data-testid="treatment-closed-badge">{t("payments.treatmentClosed")}</Badge>
            ) : canClose && ledger.has_approved_plan && (
              <Button size="sm" disabled={closing || ledger.remaining_balance > 0.009}
                onClick={closeTreatment} data-testid="close-treatment-button"
                className="bg-navy hover:bg-navy-soft text-white disabled:opacity-40">
                {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("payments.closeTreatment")}
              </Button>
            )}
          </div>
          {!ledger.has_approved_plan ? (
            <p className="text-sm text-slate-400" data-testid="ledger-no-plan">{t("payments.noPlanYet")}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div><p className="text-xs uppercase tracking-wide text-slate-400">{t("payments.agreedAmount")}</p><p className="text-lg font-heading tabular-nums" data-testid="ledger-agreed">{money(ledger.original_agreed_amount, ledger.ledger_currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-slate-400">{t("payments.discounts")}</p><p className="text-lg font-heading tabular-nums text-amber-600" data-testid="ledger-discounts">-{money(ledger.approved_discounts, ledger.ledger_currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-slate-400">{t("payments.amountDue")}</p><p className="text-lg font-heading tabular-nums" data-testid="ledger-due">{money(ledger.final_amount_due, ledger.ledger_currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-slate-400">{t("payments.paidNet")}</p><p className="text-lg font-heading tabular-nums text-emerald-600" data-testid="ledger-paid">{money(ledger.total_paid_net, ledger.ledger_currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-slate-400">{t("payments.balance")}</p><p className={`text-lg font-heading tabular-nums ${ledger.remaining_balance > 0.009 ? "text-red-600" : "text-emerald-600"}`} data-testid="ledger-balance">{money(ledger.remaining_balance, ledger.ledger_currency)}</p></div>
              </div>
              {ledger.refunds_total > 0 && <p className="text-xs text-slate-400 mt-2">{t("payments.includesRefunds")}: {money(ledger.refunds_total, ledger.ledger_currency)}</p>}
              {ledger.fx_note && <p className="text-xs text-amber-600 mt-2" data-testid="ledger-currency-note">{ledger.fx_note} · {t("payments.balanceEurBase")}: {money(ledger.remaining_balance_eur, "EUR")}</p>}
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center justify-between" data-testid="passport-panel">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-navy" />
          <div><p className="font-medium">{t("payments.passportDoc")}</p>
            <p className="text-xs text-slate-400">{passport.exists ? `${t("payments.onFile")} · ${passport.original_filename}` : t("payments.notOnFile")}</p></div>
        </div>
        <div className="flex gap-2">
          {passport.exists && <Button size="sm" variant="outline" onClick={viewPassport} data-testid="view-passport"><Download className="h-4 w-4 mr-1" />{t("common.view")}</Button>}
          {!passport.exists && (<>
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={uploadPassport} data-testid="passport-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-passport" className="bg-navy text-white"><Upload className="h-4 w-4 mr-1" />{t("common.upload")}</Button>
          </>)}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="payment-list">
        {payments.length === 0 && <div className="p-12 text-center text-slate-400">{t("payments.noPayments")}</div>}
        {payments.map((p) => (
          <div key={p.id} data-testid={`payment-${p.id}`} className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium tabular-nums">{money(p.amount, p.currency)} <span className="text-xs text-slate-400 font-normal">· {p.reference}</span></p>
              <p className="text-sm text-slate-500 capitalize">{(p.method || "").replace("_", " ")}{p.pos_reference ? ` · POS ${p.pos_reference}` : ""} · {p.cashier_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={p.evidence_status === "attached" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{p.evidence_status === "attached" ? t("payments.receiptReady") : t("payments.evidencePending")}</Badge>
              {p.receipt_reference ? (
                <Button size="sm" variant="outline" onClick={() => dlReceipt(p)} data-testid={`dl-receipt-${p.id}`}><FileText className="h-4 w-4 mr-1" />{t("payments.receipt")}</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => genReceipt(p)} data-testid={`gen-receipt-${p.id}`}><CreditCard className="h-4 w-4 mr-1" />{t("payments.generate")}</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
